import React, { useMemo, useCallback } from 'react';
import { NODE_UI, stopPropagation } from './nodeUiStyles';
import NodeAutocomplete from './NodeAutocomplete';

/**
 * NodeTimezoneSelect — Timezone autocomplete with name + UTC offset.
 * Used by: Time Delay, Schedule Start, Session Variables.
 */

const TIMEZONES = Intl.supportedValuesOf
  ? Intl.supportedValuesOf('timeZone').map((tz) => {
      let offset = '';
      try {
        const formatter = new Intl.DateTimeFormat('en', { timeZone: tz, timeZoneName: 'shortOffset' });
        const parts = formatter.formatToParts(new Date());
        const tzPart = parts.find((p) => p.type === 'timeZoneName');
        offset = tzPart ? tzPart.value : '';
      } catch { /* fallback */ }
      return { value: tz, label: `${tz} (${offset})`, offset };
    })
  : [
      'UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
      'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Asia/Tokyo', 'Asia/Shanghai',
      'Asia/Kolkata', 'Australia/Sydney', 'Pacific/Auckland',
    ].map((tz) => ({ value: tz, label: tz, offset: '' }));

const NodeTimezoneSelect = React.memo(function NodeTimezoneSelect({
  label = 'Timezone',
  value,
  onChange,
  disabled = false,
  placeholder = 'Search timezone...',
  sx,
}) {
  const selectedOption = useMemo(
    () => TIMEZONES.find((tz) => tz.value === value) || null,
    [value],
  );

  const handleChange = useCallback((newVal) => {
    onChange?.(newVal?.value || '');
  }, [onChange]);

  return (
    <div onMouseDown={stopPropagation} onClick={stopPropagation} onPointerDown={stopPropagation}>
      <NodeAutocomplete
        label={label}
        value={selectedOption}
        onChange={handleChange}
        options={TIMEZONES}
        getOptionLabel={(opt) => opt?.label || ''}
        isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
        placeholder={placeholder}
        disabled={disabled}
        sx={sx}
      />
    </div>
  );
});

export default NodeTimezoneSelect;
